import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ACTIONABLE_ORDER_STATUSES } from '../orders/components/OrderTable';
import type { MerchantOrder, MerchantOrderPage } from '../orders/types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

const REVENUE_FETCH_LIMIT = 200;
/** The trend chart's rolling window — "Evolution commandes / ventes" (14 derniers jours). */
const CHART_WINDOW_DAYS = 14;
/**
 * A real ceiling, not a guess at "enough": up to 1,000 of the most recent
 * orders are scanned before this hook gives up paginating. A shop placing
 * more than 1,000 orders within the window below is far beyond anything
 * this environment's real data has ever produced — and reaching this
 * ceiling only NARROWS the scope of what was summed, it never fabricates
 * a figure for the orders left unscanned.
 */
const ORDERS_WINDOW_MAX_PAGES = 5;

function startOfDay(reference: Date): Date {
  return new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
}

function startOfMonth(reference: Date): Date {
  return new Date(reference.getFullYear(), reference.getMonth(), 1);
}

function daysAgo(reference: Date, days: number): Date {
  const result = startOfDay(reference);
  result.setDate(result.getDate() - (days - 1));
  return result;
}

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Fetches the merchant's most recent real orders (`GET /merchant/orders`,
 * DEV §9.4) as far back as either the start of the current month or the
 * trend chart's rolling window — whichever is EARLIER — so this one fetch
 * backs both `useRevenuePeriodsQuery` (CA du jour / CA du mois) and
 * `useDailySalesSeriesQuery` (the SVG trend chart) without either hook
 * re-fetching what the other already has: same `queryKey`, deduplicated
 * by react-query regardless of how many components call it.
 *
 * Explicitly authorized by the supervisor to compute these figures
 * CLIENT-SIDE: `GET /merchant/analytics` (S17) is a fixed ALL-TIME
 * aggregate with no date parameter at all — confirmed by reading
 * merchant-analytics.controller.ts and merchant-analytics-repository.ts in
 * full, neither takes one. Orders arrive sorted `created_at DESC`
 * (order-repository.ts's own `listForTenant`), so this walks pages of the
 * API's own real maximum (`limit=200`, order.dto.ts) starting from the
 * newest and stops as soon as a page's OLDEST order falls before the
 * window's start — it never fetches more than the data actually needed.
 */
function useOrdersWindowQuery(): UseQueryResult<readonly MerchantOrder[], ApiError> {
  const { accessToken, activeShopId } = useSession();

  return useQuery<readonly MerchantOrder[], ApiError>({
    queryKey: ['orders', activeShopId, 'dashboard-window'],
    queryFn: async () => {
      const now = new Date();
      const windowStart = new Date(
        Math.min(startOfMonth(now).getTime(), daysAgo(now, CHART_WINDOW_DAYS).getTime()),
      );
      const collected: MerchantOrder[] = [];
      let offset = 0;
      for (let page = 0; page < ORDERS_WINDOW_MAX_PAGES; page += 1) {
        const result = await apiRequest<MerchantOrderPage>(
          `/merchant/orders?limit=${String(REVENUE_FETCH_LIMIT)}&offset=${String(offset)}`,
          { accessToken, shopId: activeShopId },
        );
        collected.push(...result.orders);
        const oldestOnPage = result.orders.at(-1);
        const reachedEnd = result.orders.length < REVENUE_FETCH_LIMIT;
        const pastWindowStart =
          oldestOnPage !== undefined && new Date(oldestOnPage.createdAt) < windowStart;
        if (reachedEnd || pastWindowStart) break;
        offset += REVENUE_FETCH_LIMIT;
      }
      return collected;
    },
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export interface RevenuePeriods {
  readonly todayRevenue: number;
  readonly todayOrderCount: number;
  readonly monthRevenue: number;
  readonly monthOrderCount: number;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * CA du jour / CA du mois — Sprint UX/UI Merchant Dashboard V2. CANCELLED
 * orders are excluded from both sums, the same definition
 * `MerchantAnalyticsOverview.revenue` already uses.
 */
export function useRevenuePeriodsQuery(): RevenuePeriods {
  const windowQuery = useOrdersWindowQuery();
  const orders = windowQuery.data ?? [];
  const now = new Date();
  const dayStart = startOfDay(now);
  const monthStart = startOfMonth(now);

  let todayRevenue = 0;
  let todayOrderCount = 0;
  let monthRevenue = 0;
  let monthOrderCount = 0;

  for (const order of orders) {
    if (order.status === 'CANCELLED') continue;
    const createdAt = new Date(order.createdAt);
    if (createdAt < monthStart) continue;
    monthRevenue += order.grandTotal;
    monthOrderCount += 1;
    if (createdAt >= dayStart) {
      todayRevenue += order.grandTotal;
      todayOrderCount += 1;
    }
  }

  return {
    todayRevenue,
    todayOrderCount,
    monthRevenue,
    monthOrderCount,
    isPending: windowQuery.isPending,
    isError: windowQuery.isError,
  };
}

export interface DailySalesPoint {
  readonly date: string;
  readonly orderCount: number;
  readonly revenue: number;
}

export interface DailySalesSeries {
  readonly points: readonly DailySalesPoint[];
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * "Evolution commandes / ventes" — the SVG trend chart's data, one point
 * per calendar day over the last `CHART_WINDOW_DAYS` days (oldest first),
 * INCLUDING days with zero orders (a real `0`, not an omitted day).
 * CANCELLED orders are excluded, matching every other revenue figure on
 * this dashboard.
 */
export function useDailySalesSeriesQuery(): DailySalesSeries {
  const windowQuery = useOrdersWindowQuery();
  const orders = windowQuery.data ?? [];
  const now = new Date();
  const windowStart = daysAgo(now, CHART_WINDOW_DAYS);

  const byDay = new Map<string, { orderCount: number; revenue: number }>();
  for (let i = 0; i < CHART_WINDOW_DAYS; i += 1) {
    const day = new Date(windowStart);
    day.setDate(day.getDate() + i);
    byDay.set(dateKey(day), { orderCount: 0, revenue: 0 });
  }

  for (const order of orders) {
    if (order.status === 'CANCELLED') continue;
    const createdAt = new Date(order.createdAt);
    if (createdAt < windowStart) continue;
    const key = dateKey(createdAt);
    const bucket = byDay.get(key);
    if (bucket === undefined) continue;
    bucket.orderCount += 1;
    bucket.revenue += order.grandTotal;
  }

  const points: DailySalesPoint[] = Array.from(byDay.entries()).map(([date, bucket]) => ({
    date,
    orderCount: bucket.orderCount,
    revenue: bucket.revenue,
  }));

  return {
    points,
    isPending: windowQuery.isPending,
    isError: windowQuery.isError,
  };
}

export interface ActionableOrdersCount {
  readonly count: number;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * "Commandes à traiter" — the real count of orders sitting in one of
 * `ACTIONABLE_ORDER_STATUSES` (OrderTable.tsx's own `nextMerchantStatus`
 * definition, reused rather than re-invented). One `limit=1` request per
 * status via the API's own `status` filter (`GET /merchant/orders`,
 * DEV §9.4) — only `total` from each page envelope is read, so this never
 * pulls order bodies it does not need just to produce a count.
 */
export function useActionableOrdersCountQuery(): ActionableOrdersCount {
  const { accessToken, activeShopId } = useSession();

  const queries = useQueries({
    queries: ACTIONABLE_ORDER_STATUSES.map((status) => ({
      queryKey: ['orders', activeShopId, status, 'count'],
      queryFn: () =>
        apiRequest<MerchantOrderPage>(`/merchant/orders?status=${status}&limit=1`, {
          accessToken,
          shopId: activeShopId,
        }),
      enabled: accessToken !== null && activeShopId !== null,
    })),
  });

  return {
    count: queries.reduce((sum, q) => sum + (q.data?.total ?? 0), 0),
    isPending: queries.some((q) => q.isPending),
    isError: queries.some((q) => q.isError),
  };
}
